import type { Listing } from '@repo/api';

/**
 * Urgency signal shown on a listing (card + unit page).
 * - `trending`: the unit has 2+ scheduled tours.
 * - `new`: the unit was created within the last 48 hours.
 */
export type ListingUrgency = 'trending' | 'new';

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

/**
 * Single source of truth for whether an urgency tag ("Trending" / "New") is
 * active for a listing. Used both to render the tag and to attach the
 * `urgency` analytics param when the element is visible.
 *
 * Returns `null` when no urgency tag should be shown.
 */
export function getListingUrgency(listing: Listing | null | undefined): ListingUrgency | null {
  if ((listing?.toursScheduled ?? 0) >= 2) return 'trending';

  const atCreated = listing?.activeListing?.atCreated;
  if (atCreated != null && Date.now() - new Date(atCreated).getTime() < FORTY_EIGHT_HOURS_MS) {
    return 'new';
  }

  return null;
}

/**
 * Convenience helper for spreading the urgency analytics param only when a tag
 * is active, e.g. `analytics.track('event', { ...urgencyParam(listing) })`.
 */
export function urgencyParam(listing: Listing | null | undefined): { urgency?: ListingUrgency } {
  const urgency = getListingUrgency(listing);
  return urgency ? { urgency } : {};
}
